import React, { useEffect, useState } from 'react';
import { Bell, Plus, Trash2, Save } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import { eventProcessingService, processingError, CameraNotificationRule, CameraNotificationSettings } from '../../services/event-processing.service';

const EVENT_TYPES = [
  ['motion', 'Motion'], ['intrusion', 'Intrusion'], ['line_crossing', 'Line crossing'],
  ['face_detected', 'Face detection'], ['license_plate', 'License plate'], ['tampering', 'Tampering'], ['video_loss', 'Video loss'],
];

export const newNotificationRule = (): CameraNotificationRule => ({
  name: 'Repeated event', enabled: true, event_types: [], min_count: 3, window_seconds: 60, cooldown_seconds: 300,
});

const CameraNotificationRules: React.FC<{ cameraId: number }> = ({ cameraId }) => {
  const [settings, setSettings] = useState<CameraNotificationSettings>({ enabled: false, rules: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoaded(false); setError('');
    eventProcessingService.getCameraSettings(cameraId).then(data => { if (active) { setSettings(data); setLoaded(true); } })
      .catch(err => { if (active) setError(processingError(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cameraId]);

  const change = (index: number, patch: Partial<CameraNotificationRule>) => {
    setSettings(current => ({ ...current, rules: current.rules.map((rule, i) => i === index ? { ...rule, ...patch } : rule) }));
    setSaved(false);
  };
  const save = async () => {
    if (!loaded) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const data = await eventProcessingService.saveCameraSettings(cameraId, settings);
      setSettings(data); setSaved(true);
    } catch (err) { setError(processingError(err)); }
    finally { setSaving(false); }
  };

  return <Card padding="lg">
    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
      <div className="flex gap-3"><div className="p-2 rounded-xl bg-accent-primary/10 text-accent-primary h-fit"><Bell size={22} /></div>
        <div><h2 className="text-lg font-semibold text-text-primary">Camera notifications</h2>
          <p className="text-sm text-text-muted mt-1">Turn repeated detections into useful alerts, with a quiet period between notifications.</p></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
        <input type="checkbox" className="w-4 h-4 accent-accent-primary" checked={settings.enabled} disabled={!loaded}
          onChange={e => { setSettings(current => ({ ...current, enabled: e.target.checked, rules: e.target.checked && !current.rules.length ? [newNotificationRule()] : current.rules })); setSaved(false); }} />
        Enable notifications
      </label>
    </div>
    {loading ? <p className="text-sm text-text-muted">Loading notification rules…</p> : <>
      {error && <Alert variant="error" message={error} />}
      {saved && <Alert variant="success" message="Notification rules saved." />}
      <div className="space-y-4 mt-4">
        {settings.rules.map((rule, index) => <div key={rule.id || index} className="rounded-xl border border-border-default bg-surface-secondary/40 p-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">Scenario {index + 1}</span>
            <div className="flex items-center gap-4"><label className="flex items-center gap-2 text-sm text-text-primary"><input type="checkbox" checked={rule.enabled} onChange={e => change(index, { enabled: e.target.checked })} />Active</label>
              <Button type="button" variant="ghost" size="sm" aria-label={`Remove scenario ${index + 1}`} onClick={() => { setSettings(current => ({ ...current, rules: current.rules.filter((_, i) => i !== index) })); setSaved(false); }}><Trash2 size={16} /></Button>
            </div>
          </div>
          <Input label="Scenario name" value={rule.name} maxLength={100} onChange={e => change(index, { name: e.target.value })} fullWidth />
          <fieldset><legend className="field-label">Event types</legend>
            <label className="flex items-center gap-2 text-sm text-text-primary mb-3"><input type="checkbox" checked={!rule.event_types.length} onChange={e => change(index, { event_types: e.target.checked ? [] : ['intrusion'] })} />All event types, grouped separately</label>
            <div className="flex flex-wrap gap-2">{EVENT_TYPES.map(([key, label]) => <label key={key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer ${rule.event_types.includes(key) ? 'border-accent-primary/50 bg-accent-primary/10 text-text-primary' : 'border-border-default text-text-muted'}`}>
              <input type="checkbox" checked={rule.event_types.includes(key)} onChange={e => change(index, { event_types: e.target.checked ? [...rule.event_types, key] : rule.event_types.filter(value => value !== key) })} />{label}
            </label>)}</div>
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input type="number" min={2} max={100} label="Similar events" value={rule.min_count} onChange={e => change(index, { min_count: Number(e.target.value) })} />
            <Input type="number" min={10} max={3600} label="Within (seconds)" value={rule.window_seconds} onChange={e => change(index, { window_seconds: Number(e.target.value) })} />
            <Input type="number" min={30} max={86400} label="Quiet period (seconds)" value={rule.cooldown_seconds} onChange={e => change(index, { cooldown_seconds: Number(e.target.value) })} />
          </div>
          <p className="text-xs text-text-muted">Notify when this camera reports {rule.min_count} events of the same type within {rule.window_seconds} seconds. Wait {rule.cooldown_seconds} seconds before another alert for that type.</p>
        </div>)}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
        <Button type="button" variant="secondary" size="sm" disabled={!loaded || settings.rules.length >= 10} leftIcon={<Plus size={16} />} onClick={() => { setSettings(current => ({ ...current, rules: [...current.rules, newNotificationRule()] })); setSaved(false); }}>Add scenario</Button>
        <Button type="button" disabled={!loaded} isLoading={saving} leftIcon={<Save size={16} />} onClick={save}>Save notification rules</Button>
      </div>
      <p className="text-xs text-text-muted mt-4">Alerts appear in the notification bell. Enable email and configure recipients in Company → Notification email. Historical imports do not trigger live alerts.</p>
    </>}
  </Card>;
};

export default CameraNotificationRules;
